import { AutogenLayoutRecurringPayAdjustment } from "./autogen/AutogenLayoutRecurringPayAdjustment";
import {
    DataHeader, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    DataSourceValidationEvent, DropdownSelectionEvent, LookupModelSelectionEvent, PrintableEvent, Snackbar,
    ValidationResult
} from "@mcleod/components";
import {
    Api, DisplayType, DisplayValue, HorizontalAlignment, LogManager, StringUtil, CurrencyUtil, CurrencySettings
} from "@mcleod/core";
import { AutoMatchProfile } from "@mcleod/dispatch/src/AutoMatchProfile";

const log = LogManager.getLogger("lme.settlement.RecurringPayAdjustment");

export class RecurringPayAdjustment extends AutogenLayoutRecurringPayAdjustment {
    onLoad(): void | Promise<void> {
        CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
        this.textboxGlid.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            if (mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                component: this.textboxGlid,
                isValid: true
            };
            const lookupModelData = this.textboxGlid.getFirstLookupModelData();
            const isEscrowGlAccount: boolean = lookupModelData?.getBoolean("is_escrow", false);
            const isEscrowDeductionType = this.activeRow.get("deduction_type") === "T" || this.activeRow.get("deduction_type") === "F";
            if (isEscrowDeductionType && !isEscrowGlAccount) {
                result.isValid = false;
                result.validationMessage = "GL account must be an escrow account for Escrow type recurring adjustments.";
            }
            const isEarningAdjustmentType = this.activeRow.get("deduct_code.code_type") === "E" || this.activeRow.get("deduct_code.code_type") === "P";
            if (isEarningAdjustmentType && isEscrowGlAccount) {
                result.isValid = false;
                result.validationMessage = "Cannot use escrow GL account with Earning or Per Diem type adjustments.";
            }
            return result;
        };
        this.textboxGlid.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                this.setEscrowBalanceInfo();
                this.textboxGlid.validateSimple();
            }
        });
        this.layoutTableComment.displayCopyOrderFields = false;
        this.setComponentListeners();
        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            this.validateEscrowAdjustments(event);
            this.validateRateEntry(event);
        });
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.activeRow.get("deduction_type") === "T" || this.activeRow.get("deduction_type") === "F") {
                return this.validateOverlappingEscrowDeductions();
            }
        };
        this.textboxTractorId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.textboxTractorId.validateSimple();
            }
        });
        this.textboxTractorId.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            const result: ValidationResult = {
                component: this.textboxTractorId,
                isValid: true
            }
            const lookupModelData = this.textboxTractorId.getFirstLookupModelData();
            if (mode === DataSourceMode.SEARCH || !lookupModelData) {
                return result;
            }
            if (lookupModelData.get("pay_owner") === "O" || lookupModelData.get("pay_owner") === "B") {
                if (lookupModelData.get("owner") !== this.activeRow.get("payee_id")) {
                    result.isValid = false;
                    result.validationMessage = "Payee is not the owner of the tractor.";
                }
            }
            return result;
        }
        this.textboxFrequency.addlValidationCallback = () => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                component: this.textboxFrequency,
                isValid: true
            };
            const frequency = this.activeRow.get("frequency");
            const codeType = this.activeRow.get("deduct_code.code_type");

            if (!frequency || !codeType) {
                return result;
            }

            // Copy logic from EntryAbstractRecurDeduct.checkFrequencyCodeConflict()
            // 1) Percent of net cannot be 'Before Tax' type
            if (frequency === "N" && codeType === "B") {
                result.isValid = false;
                result.validationMessage = result.caption = "Percent of net cannot be 'Before Tax' type";
                return result;
            }
            // 2) Reimbursement type not compatible with selected frequency (percentage-based)
            if ((frequency === "N" || frequency === "G" || frequency === "O") && codeType === "R") {
                result.isValid = false;
                result.validationMessage = result.caption = "Reimbursement type not compatible with selected frequency";
                return result;
            }
            // 3) Earning type not compatible with selected frequency (percent of net/gross)
            if ((frequency === "N" || frequency === "G") && codeType === "E") {
                result.isValid = false;
                result.validationMessage = result.caption = "Earning type not compatible with selected frequency";
                return result;
            }
            // 4) Only after-tax deductions are allowed for garnishments
            if ((frequency === "R" || frequency === "O") && codeType !== "D") {
                result.isValid = false;
                result.validationMessage = result.caption = "Only after-tax deductions are allowed for garnishments.";
                return result;
            }

            return result;
        }
    }

    private setComponentListeners() {
        this.textboxCurrency.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.changeCurrencyCode();
            }
        });
        this.textboxTotalToDate.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishCurrent.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishNetAmt.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxLoanBalance.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxOrigLoanAmt.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxEscrowThreshold.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishArrears.addBlurListener(() => {
            this.changeCurrencyCode();
        });
    }

    sourceDrsRecurDeductAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.onDeductCodeIdSelection();
            this.setEscrowBalanceInfo();
        }
    }

    sourceDrsRecurDeductAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
            this.onFrequencySelection();
            this.onDeductionTypeSelection();
            if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                if (this.textboxCurrency.visible && this.textboxCurrency.enabled) {
                    this.textboxCurrency.enabled = this.activeRow.isNull("last_taken") || (!this.activeRow.isNull("last_taken") && this.activeRow.isNull("currency"));
                    if (!this.textboxCurrency.enabled) {
                        this.textboxCurrency.disabledTooltip = "Currency cannot be updated because pay adjustments have already been created from this record.";
                    }
                }
            }
        } else if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.panelEscrowLastUpdated.visible = false;
        }
    }

    textboxPayeeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            const selection = event.selection;
            this.activeRow.set("drs_payee.type_of", selection.get("drs_payee.type_of"));
            this.activeRow.setValueAndLookupModelData("currency", selection.get("currency"), selection.getLookupModelData("currency"));
            this.changeCurrencyCode();
            this.setEscrowBalanceInfo();
        }
    }

    private changeCurrencyCode() {
        if (!CurrencySettings.getSingleton().multiCurrencyEnabled()) return;
        const currencyCode = this.activeRow.get("currency");
        const [totalToDate, garnishNetAmount, garnishCurrent, arrearsAmount, loanBalance, originalLoanAmount, escrowThreshold] = [
            this.activeRow.getCurrency("total_to_date"),
            this.activeRow.getCurrency("garnish_net_amt"),
            this.activeRow.getCurrency("garnish_current"),
            this.activeRow.getCurrency("garnish_arrears"),
            this.activeRow.getCurrency("loan_balance"),
            this.activeRow.getCurrency("orig_loan_amt"),
            this.activeRow.getCurrency("escrow_threshold")
        ];

        if (totalToDate) {
            totalToDate.setCurrencyCode(currencyCode);
            this.activeRow.set("total_to_date", totalToDate);
        }

        if (garnishNetAmount) {
            garnishNetAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_net_amt", garnishNetAmount);
        }

        if (garnishCurrent) {
            garnishCurrent.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_current", garnishCurrent);
        }

        if (arrearsAmount) {
            arrearsAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_arrears", arrearsAmount);
        }

        if (loanBalance) {
            loanBalance.setCurrencyCode(currencyCode);
            this.activeRow.set("loan_balance", loanBalance);
        }

        if (originalLoanAmount) {
            originalLoanAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("orig_loan_amt", originalLoanAmount);
        }

        if (escrowThreshold) {
            escrowThreshold.setCurrencyCode(currencyCode);
            this.activeRow.set("escrow_threshold", escrowThreshold);
        }
    }

    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            const selection = event.selection;
            this.activeRow.set("deduct_code.code_type", selection.get("code_type"));
            this.activeRow.set("glid", selection.get("glid"));
            AutoMatchProfile.applyLookupModelData(this.textboxGlid, this.activeRow).then(r => {
                this.setEscrowBalanceInfo();
            });
            this.activeRow.set("amount", selection.getCurrency("amount")?.amount);
            this.onDeductCodeIdSelection();
            this.activeRow.set("short_desc", this.activeRow.getFirstLookupModelData("deduct_code_id")?.get("short_desc"));
        }
    }

    onDeductCodeIdSelection() {
        this.textboxLoanBalance.required = this.activeRow.get("deduction_type") === "L";
    }

    textboxFrequencyAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (this.textboxEscrowThreshold.enabled && event.selection.value !== "C") {
                Snackbar.showWarningSnackbar("Frequency must be Cyclical for Escrow type recurring deductions.", { targetPanel: this });
                this.activeRow.set("frequency", "C");
                this.onFrequencySelection();
                return;
            }
            const newFrequency = event.selection.value;
            if (this.mainDataSource.mode !== DataSourceMode.ADD && (newFrequency === "O" || newFrequency === "R")) {
                Api.search("lme/settlement/validate-pay-adjustment-frequency", {
                    recurring_pay_adjustment_id: this.activeRow.get("id")
                }).then((response) => {
                    const errorMessage = response.data?.[0]?.error_message;
                    if (errorMessage) {
                        Snackbar.showWarningSnackbar(errorMessage, { targetPanel: this });
                        this.activeRow.set("frequency", this.activeRow.originalData?.["frequency"]);
                    } else {
                        this.onFrequencySelection();
                    }
                });
            } else {
                this.onFrequencySelection();
            }
        }
    }

    onFrequencySelection() {
        this.showGarnishments();
        this.textboxCycleId.required = this.textboxCycleId.enabled = this.activeRow.get("frequency") === "C" || this.activeRow.get("frequency") === "R";
        if (!this.textboxCycleId.required) {
            this.activeRow.set("cycle_id", null);
        }
        this.textboxAmount.required = this.textboxAmount.enabled = !(this.activeRow.get("frequency") === "G" || this.activeRow.get("frequency") === "N" || this.activeRow.get("frequency") === "O");
        if (!this.textboxAmount.required) {
            this.activeRow.set("amount", null);
        }
        this.textboxPercentage.required = this.textboxPercentage.enabled = this.activeRow.get("frequency") === "G" || this.activeRow.get("frequency") === "N" || this.activeRow.get("frequency") === "O";
        if (!this.textboxPercentage.required) {
            this.activeRow.set("percentage", null);
        }
        if (this.activeRow.get("frequency") === "O") {
            this.textboxPercentage.required = false;
        }
    }

    showGarnishments() {
        this.tabGarnishments.visible = this.activeRow.get("frequency") === "O" || this.activeRow.get("frequency") === "R";
        this.textboxGarnishPriority.required = this.tabGarnishments.visible;
        this.textboxVendorId.required = this.tabGarnishments.visible;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    textboxDeductionTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            this.onDeductionTypeSelection();
        }
    }

    onDeductionTypeSelection() {
        this.textboxLoanBalance.enabled = this.textboxLoanBalance.required = this.textboxOrigLoanAmt.enabled = this.activeRow.get("deduction_type") === "L";
        this.textboxEscrowThreshold.enabled = this.activeRow.get("deduction_type") === "T" || this.activeRow.get("deduction_type") === "F";
        if (!this.textboxLoanBalance.enabled) {
            this.activeRow.set("loan_balance", null);
            this.activeRow.set("orig_loan_amt", null);
        }
        if (!this.textboxEscrowThreshold.enabled) {
            this.activeRow.set("escrow_threshold", null);
        }
        if (this.textboxEscrowThreshold.enabled) {
            this.activeRow.set("frequency", "C");
            this.onFrequencySelection();
        }
    }

    setEscrowBalanceInfo() {
        const payeeId = this.activeRow.get("payee_id");
        const glId = this.activeRow.get("glid");
        const lookupModelData = this.textboxGlid.getFirstLookupModelData();
        this.panelEscrowLastUpdated.visible = lookupModelData?.getBoolean("is_escrow", false) === true;
        if (payeeId && glId && this.panelEscrowLastUpdated.visible) {
            Api.search("lme/settlement/escrow-balance", { payee_id: payeeId, glid: glId }).then((response) => {
                const data = response.data?.[0];
                if (data) {
                    this.textboxEscrowBalance.text = DisplayValue.getCurrencyDisplayValue(data.amount);
                    this.textboxEscrowLastUpdated.text = DisplayValue.getDateDisplayValue(data.last_updated_date, DisplayType.DATE);
                }
            }).catch((error) => {
                log.error("Error fetching escrow balance:", error);
            });
        }
    }

    private validateEscrowAdjustments(event: DataSourceValidationEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        const deductionType = this.activeRow.get("deduction_type");
        const payAdjustmentType = this.activeRow.get("deduct_code.code_type");
        const frequency = this.activeRow.get("frequency");
        const glAccountData = this.textboxGlid.getFirstLookupModelData();
        const isEscrowGLAccount = glAccountData?.getBoolean("is_escrow", false);
        const escrowThreshold = this.activeRow.getCurrency("escrow_threshold");

        // Validation for "Escrow to Threshold" or "Escrow to Full Amount" adjustment types
        if (deductionType === "T" || deductionType === "F") {
            // Check if pay adjustment type is "Before-tax deduction" or "After-tax deduction"
            if (payAdjustmentType !== "B" && payAdjustmentType !== "D") {
                event.validationResults.push({
                    component: this.textboxDeductCodeId,
                    isValid: false,
                    caption: "Pay adjustment type must be before-tax or after-tax deduction for an Escrow adjustment type."
                });
            }

            // Check escrow threshold is not less than $0
            if (escrowThreshold && escrowThreshold.amount < 0) {
                event.validationResults.push({
                    component: this.textboxEscrowThreshold,
                    isValid: false,
                    caption: "The Escrow Threshold field does not allow an amount less than $0."
                });
            }
        }

        // Validation for "Standard" or "Loan" adjustment types with escrow GL Account
        if ((deductionType === "S" || deductionType === "L") && isEscrowGLAccount) {
            // Check if pay adjustment type is "Earning" or "Per Diem"
            if (payAdjustmentType === "E" || payAdjustmentType === "P") {
                event.validationResults.push({
                    component: this.textboxDeductCodeId,
                    isValid: false,
                    caption: "Cannot use an escrow GL account with Earning or Per Diem type deductions."
                });
            }

            // Check if frequency is "Net Percent", "Garnishment - Cyclical", or "Garnishment - Other"
            if (frequency === "N" || frequency === "R" || frequency === "O") {
                event.validationResults.push({
                    component: this.textboxFrequency,
                    isValid: false,
                    caption: "Frequency is not valid when an escrow GL account is used."
                });
            }
        }
    }

    private async validateOverlappingEscrowDeductions(): Promise<boolean> {
        const payeeId = this.activeRow.get("payee_id");
        const glId = this.activeRow.get("glid");
        const startDate = this.activeRow.get("deduct_start_date");
        const endDate = this.activeRow.get("deduct_end_date");
        const currentRecordId = this.activeRow.get("id");

        if (!payeeId || !glId) {
            return;
        }

        // Use the new dedicated endpoint for escrow overlap validation
        const searchParams: any = {
            payee_id: payeeId,
            glid: glId,
            deduct_start_date: startDate,
            deduct_end_date: endDate
        };

        // Include current record ID if available to exclude it from validation
        if (currentRecordId) {
            searchParams.current_record_id = currentRecordId;
        }

        const response = await Api.search("lme/settlement/validate-escrow-overlap", searchParams);
        if (response.data && response.data.length > 0) {
            // The endpoint returns the error message directly
            const validationError = response.data[0];
            Snackbar.showWarningSnackbar(validationError.error_message, { targetPanel: this });
            return false;
        }
        return true;
    }

     private validateRateEntry(event: DataSourceValidationEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        const frequency: string = this.activeRow.get("frequency");
        if (frequency !== "O") return; // Only applies to Garnishment - Other

        const percent = this.activeRow.get("percentage");
        const garnishPercent = this.activeRow.get("garnish_percent");

        const hasPercent: boolean = !StringUtil.isEmptyString(percent);
        const hasGarnishNetAmt: boolean = !this.activeRow.isNull("garnish_net_amt"); // presence is enough (amount can be 0)
        const hasGarnishPercent: boolean = !StringUtil.isEmptyString(garnishPercent);

        if (!(hasPercent || hasGarnishNetAmt || hasGarnishPercent)) {
            event.validationResults.push({
                component: this.textboxPercentage,
                isValid: false,
                caption: "At least one of the following fields must be entered: Percent, Percent Limitation, or Guaranteed Disposable Income."
            });
        }
    }

    textboxEscrowBalanceOnPrintable(event: PrintableEvent) {
        event.printableLabel.align = HorizontalAlignment.LEFT;
    }
}
